import { jsx, jsxs } from "react/jsx-runtime";
import { DSControlledDateTimePicker } from "@elliemae/ds-form-date-time-picker";
import _ from "lodash";
import { useFormState } from "react-hook-form";
import { DSFormLayoutBlockItem } from "@elliemae/ds-form-layout-blocks";
import { Grid } from "@elliemae/ds-grid";
import { useState, useEffect } from "react";
import { DSControlledToggle } from "@elliemae/ds-form-toggle";
import { parse, parseISO, isValid, format } from "date-fns";
import { useFieldVisibilityAndDisability } from "../utils/fieldVisibilityAndDisability";
import {
  getDefaultFocusedDate,
  useLoanDataFieldSelectorHook
} from "../utils/utils";
const EffectiveDate = ({
  renderedField,
  schemaForm,
  rules,
  name,
  label,
  required,
  pathValue,
  feedbackMessage,
  ui,
  visible,
  disabled,
  disableCheck,
  visibleCheck,
  preview
}) => {
  const effectiveDateVal = useLoanDataFieldSelectorHook(pathValue)?.value ?? "";
  const hasInitialDate = Boolean(effectiveDateVal);
  const [dateTimeString, setDateTimeString] = useState("");
  const [timeValidationError, setTimeValidationError] = useState("");
  const [checked, setChecked] = useState(hasInitialDate);
  const { errors } = useFormState({ control: schemaForm.control });
  const errorMessage = _.get(errors, pathValue)?.message;
  const { isVisible, isDisabled } = useFieldVisibilityAndDisability({
    schemaForm,
    rules,
    visible,
    disabled,
    visibleCheck,
    disableCheck,
    preview
  });
  const dateTimePickerFormat = "MM/dd/yyyy hh:mm a";
  const parseEffectiveDate = (value) => {
    if (!value || typeof value !== "string") return "";
    let parsed = parseISO(value);
    if (!isValid(parsed)) {
      parsed = parse(value, "MM/dd/yyyy hh:mm:ss a", /* @__PURE__ */ new Date());
    }
    if (!isValid(parsed)) {
      parsed = parse(value, dateTimePickerFormat, /* @__PURE__ */ new Date());
    }
    return isValid(parsed) ? format(parsed, dateTimePickerFormat) : "";
  };
  const validateTimeRequired = (dateTimeValue) => {
    if (!dateTimeValue) return "";
    const emptyTimePattern = /^\d{2}\/\d{2}\/\d{4}\s__:__\s__$/;
    const missingAmPmPattern = /^\d{2}\/\d{2}\/\d{4}\s\d{2}:\d{2}\s__$/;
    if (emptyTimePattern.test(dateTimeValue)) {
      return "Time is required";
    }
    if (missingAmPmPattern.test(dateTimeValue)) {
      return "AM/PM is required";
    }
    return "";
  };
  useEffect(() => {
    if (hasInitialDate) {
      const parsedDateTime = parseEffectiveDate(effectiveDateVal);
      setDateTimeString(
        typeof effectiveDateVal === "string" ? parsedDateTime : ""
      );
      renderedField.onChange(parsedDateTime);
    }
  }, [effectiveDateVal]);
  return /* @__PURE__ */ jsxs(Grid, { span: ui.col_size, style: { display: isVisible ? "block" : "none" }, children: [
    checked && /* @__PURE__ */ jsx(
      DSFormLayoutBlockItem,
      {
        inputID: `epps-field-id-${name}`,
        label,
        required,
        feedbackMessage,
        hasError: !!errorMessage || !!timeValidationError,
        validationMessage: timeValidationError || errorMessage,
        children: /* @__PURE__ */ jsx(
          DSControlledDateTimePicker,
          {
            ...renderedField,
            dateTime: dateTimeString,
            onDateTimeChange: (newDateTime) => {
              setDateTimeString(newDateTime);
              renderedField.onChange(newDateTime);
              if (timeValidationError) {
                setTimeValidationError("");
              }
            },
            onBlur: () => {
              schemaForm.setValue(pathValue, dateTimeString);
              const validationError = validateTimeRequired(dateTimeString);
              setTimeValidationError(validationError);
            },
            onCalendarOpenFocusedDay: getDefaultFocusedDate(),
            disabled: isDisabled,
            id: pathValue,
            type: ui?.type,
            style: { width: "fit-content" }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      DSFormLayoutBlockItem,
      {
        label: "Use Historic Pricing",
        inputID: `epps-field-id-toggle-${name}`,
        leftLabel: true,
        children: /* @__PURE__ */ jsx(
          DSControlledToggle,
          {
            checked,
            labelOn: "Yes",
            labelOff: "No",
            size: "s",
            onChange: () => {
              setChecked((prev) => {
                const newChecked = !prev;
                if (!newChecked) {
                  schemaForm.setValue(pathValue, null);
                  setDateTimeString("");
                  setTimeValidationError("");
                }
                return newChecked;
              });
            },
            containerProps: {},
            id: `toggle-${name}`
          }
        )
      }
    )
  ] });
};
export {
  EffectiveDate
};
